// Shared system-prompt + tool definitions for the seller "ask Cisero" chat
// surfaces — the inline composer in ChatJourney and the floating panel in
// AskExpert. Both have access to the same journey callbacks (advance/back/
// jump/update-any-stage), so both can offer the same tools.
import { propFactsLine } from "./property";
import { STAGES } from "../stages";
import { getStageState } from "./journey";

export const chatSystem = (stage, journey) => {
  const facts = propFactsLine(journey.property);
  const stageList = STAGES.map((s) => s.id).join(", ");
  const statusLine = STAGES
    .map((s) => `${s.title}: ${getStageState(journey, s.id).status}`)
    .join(" | ");
  return `You are Cisero, the AI assistant inside 6zero.ai's guided home-selling `
    + `flow, helping a seller with their property at ${journey.address}. `
    + `They're currently on the "${stage.title}" stage (${stage.blurb})`
    + (facts ? ` Known facts: ${facts}.` : ".")
    + `\n\nFull journey status (all 12 stages, in order): ${statusLine}. `
    + `"done" means finished, "in_progress"/"awaiting" means started but not `
    + `finished, "pending" means not started yet. Use this to answer questions `
    + `like "what's outstanding" or "what's left" with SPECIFIC stage names `
    + `pulled from this list, not generic guesses about the current stage's `
    + `possible sub-items. If they ask what's pending, name the actual pending/`
    + `in_progress stages and suggest what to tackle next — be proactive and `
    + `directive, not just reactive.\n\n`
    + `HARD RULE: whenever you list stage statuses (any time you name 2+ `
    + `stages with their status, e.g. answering "what's left" or "what's `
    + `outstanding"), prefix EVERY stage name with a colored status dot — `
    + `✅ for done, 🟡 for in_progress/awaiting, ⚪ for pending. Always as a `
    + `flat "- " bullet list, one stage per line, never grouped under `
    + `separate "In Progress:" / "Not Started:" headers. Example: "- ✅ `
    + `**Fair-price valuation** – complete\\n- 🟡 **Disclosures** – in `
    + `progress\\n- ⚪ **Staging** – not started". Do this even for a single `
    + `stage mention when its status is relevant to the answer.\n\n`
    + `Answer their questions directly and conversationally, grounded in this `
    + `context.\n\n`
    + `You have tools to actually move and act for them: advance_stage, `
    + `go_back_stage, jump_to_stage, and regenerate_stage. jump_to_stage goes `
    + `DIRECTLY to any of these stages in one step, in any order — ${stageList} `
    + `— there is no sequential requirement and nothing "in between" to pass `
    + `through. Never describe a multi-step path through other stages first.\n\n`
    + `HARD RULE: when the seller's intent is clear, call the tool in that `
    + `same turn. Do not ask "would you like me to", "should I", or any other `
    + `confirmation question before acting — just do it, then tell them what `
    + `you did. Example of what NOT to do: "Would you like me to jump you to `
    + `Photography? You'd pass through Staging first." Example of correct `
    + `behavior: call jump_to_stage(photography) immediately, then reply `
    + `"→ Jumped to Photography." Keep the accompanying text under 10 words `
    + `whenever you call a tool — the action already did the work, don't `
    + `re-explain it in a paragraph.\n\n`
    + `NEVER cite the current stage or journey order as a reason to defer a `
    + `request — no "photography comes later in your journey", no "you're `
    + `currently on X so Y isn't available yet". Every stage is reachable `
    + `right now via jump_to_stage regardless of where they are. If a request `
    + `is ambiguous, ask ONE short clarifying question about what they mean — `
    + `never frame that question around stage sequencing or make them wait `
    + `for "later". Only ask a clarifying question if you genuinely cannot `
    + `tell which stage or action they mean. Never tell them to go click a `
    + `button themselves when a tool can do it for them. Keep responses under `
    + `120 words.\n\n`
    + `You are a genuine subject-matter expert on residential real estate — `
    + `not limited to this app's stages. Answer confidently and specifically `
    + `on: financing and mortgages (rate locks, pre-approval vs pre-qual, `
    + `points, contingency timing), negotiation tactics (escalation clauses, `
    + `love letters, multiple-offer strategy, counter-offer math), inspection `
    + `and appraisal issues (repair credits vs price reduction, appraisal `
    + `gap coverage, re-inspection), disclosure and legal basics (what TDS/ `
    + `SPQ/NHD actually cover, material-fact disclosure duty, lead-paint `
    + `rules for pre-1978 homes), escrow and closing mechanics (who pays what `
    + `closing costs, typical escrow length, title/settlement steps), capital `
    + `gains and 1031 exchanges at a high level, and market-timing tradeoffs. `
    + `Give a real, specific answer first — never deflect a genuine question `
    + `with "consult a professional" as the whole answer; add that caveat `
    + `only after actually answering, and only where it's genuinely `
    + `warranted (tax/legal specifics), never as a substitute for helping.`;
};

export const navigationTools = [
  {
    name: "advance_stage",
    description: "Move the seller forward to the next stage in their selling journey.",
    input_schema: { type: "object", properties: {}, required: [] },
  },
  {
    name: "go_back_stage",
    description: "Move the seller back to the previous stage in their selling journey.",
    input_schema: { type: "object", properties: {}, required: [] },
  },
  {
    name: "jump_to_stage",
    description: "Jump the seller directly to a specific named stage in their selling journey.",
    input_schema: {
      type: "object",
      properties: {
        stage_id: { type: "string", enum: STAGES.map((s) => s.id), description: "The stage id to jump to." },
      },
      required: ["stage_id"],
    },
  },
  {
    name: "regenerate_stage",
    description: "Force a fresh AI regeneration of a stage's generated content (fair-price valuation, or listing copy), even if the seller isn't currently on it. Also jumps them there so they see the result. Only valuation and listing are safe to fully regenerate this way — other stages mix generated content with the seller's own progress (vendor bookings, interview answers) that a wipe would destroy, so those aren't offered.",
    input_schema: {
      type: "object",
      properties: {
        stage_id: { type: "string", enum: ["valuation", "listing"], description: "The stage id to regenerate." },
      },
      required: ["stage_id"],
    },
  },
];

// Executes any tool calls the model made and returns a short human-readable
// note describing what happened (appended to the assistant's reply).
export const runTools = (toolCalls, { onAdvance, onBack, onJump, onUpdateStage }) => {
  const notes = [];
  for (const call of toolCalls || []) {
    if (call.name === "advance_stage") {
      onAdvance?.();
      notes.push("→ Moved to the next stage.");
    } else if (call.name === "go_back_stage") {
      onBack?.();
      notes.push("→ Moved back a stage.");
    } else if (call.name === "jump_to_stage") {
      const idx = STAGES.findIndex((s) => s.id === call.input?.stage_id);
      if (idx >= 0) {
        onJump?.(idx);
        notes.push(`→ Jumped to ${STAGES[idx].title}.`);
      }
    } else if (call.name === "regenerate_stage") {
      const idx = STAGES.findIndex((s) => s.id === call.input?.stage_id);
      if (idx >= 0) {
        // Clearing data + status resets the stage's own auto-run-on-mount
        // effect (fired.current is per-instance and remounts on navigation),
        // so it regenerates itself fresh — no need to reach into each
        // stage's internal generation function.
        onUpdateStage?.(STAGES[idx].id, { status: "pending", data: null });
        onJump?.(idx);
        notes.push(`→ Regenerating ${STAGES[idx].title}…`);
      }
    }
  }
  return notes.join(" ");
};
